import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { Group } from "@dashboard/devices/types/index";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { getGroupsForAggregation } from "@dashboard/groups/utils";
import { Theme, Typography } from "@mui/material";
import semverService from "@services/semverService/semverService";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTable, MinimalTableCell, MinimalTableContainer, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { TableHeaderRows } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { TableRowCells } from "@shared/table/TableRowCells";
import {
    createColumnHelper,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";

import DevicesTableItem from "./TableItem";

const useStyles = makeStyles()((muiTheme: Theme) => ({
    container: { height: "100%" },
    table: {
        flex: 1,
        minHeight: 0,
    },
    pagination: {
        marginTop: "auto",
        flexShrink: 0,
        borderTop: `1px solid ${muiTheme.palette.emteria.border.secondary}`,
    },
}));

type OperatingSystemWithCount = {
    version: string;
    count: number;
};

const columnHelper = createColumnHelper<OperatingSystemWithCount>();

const getColumns = () => {
    return [
        columnHelper.accessor("version", {
            id: "version",
            header: i18next.t("pages:overviewPage.table.header.version"),
            cell: (info) => (
                <MinimalTableCell>
                    <Typography>{info.getValue<string>()}</Typography>
                </MinimalTableCell>
            ),
        }),
        columnHelper.accessor("count", {
            id: "count",
            header: i18next.t("pages:overviewPage.table.header.devices"),
            cell: (info) => {
                const version = info.row.original.version;
                const count = info.getValue<number>();
                const filteredGroups = info.table.options.meta?.filteredGroupIds || [];
                return <DevicesTableItem count={count} version={version} selectedGroupIds={filteredGroups} />;
            },
            meta: {
                align: "right",
            },
        }),
    ];
};

const compareOsVersions = (a: OperatingSystemWithCount, b: OperatingSystemWithCount): number => {
    if (a.count !== b.count) {
        return b.count - a.count;
    }

    return semverService.compare(b.version, a.version);
};

const aggregateOsVersionCountsFromGroups = (groups: Group[], groupIds: number[]): OperatingSystemWithCount[] => {
    const targetGroups = getGroupsForAggregation(groupIds, groups);

    const versionCounts: Record<string, number> = {};
    targetGroups.forEach((group) => {
        (group.statistics?.osVersions ?? []).forEach(({ version, count }) => {
            versionCounts[version] = (versionCounts[version] || 0) + count;
        });
    });

    return Object.entries(versionCounts)
        .map(([version, count]) => ({ version, count }))
        .sort(compareOsVersions);
};

type Props = {
    groupIds: number[];
};

export const OperatingSystemsCard = ({ groupIds }: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);
    const [sorting, setSorting] = useState<SortingState>([{ id: "count", desc: true }]);

    const groups = useSelector(selectGroups);
    const filteredGroups = !groupIds.length ? groups.map((group) => group.id) : groupIds;

    const osVersionCounts = React.useMemo(
        () => aggregateOsVersionCountsFromGroups(groups, filteredGroups),
        [groups, filteredGroups],
    );

    const table = useReactTable<OperatingSystemWithCount>({
        data: osVersionCounts,
        columns: getColumns(),
        initialState: {
            pagination: {
                pageSize: 8,
            },
        },
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        meta: {
            filteredGroupIds: filteredGroups,
        },
    });

    const { pageSize, pageIndex } = table.getState().pagination;

    return (
        <SectionPanel disablePadding className={classes.container}>
            <MinimalTableContainer plain className={classes.table}>
                <MinimalTable stickyHeader>
                    <MinimalTableHeader
                        title={{
                            label: t("pages:overviewPage.cards.operatingSystems.title"),
                            colSpan: table.getVisibleLeafColumns().length,
                        }}
                    >
                        <TableHeaderRows headerGroups={table.getHeaderGroups()} />
                    </MinimalTableHeader>
                    <TableBody<OperatingSystemWithCount> table={table} noDataMessage={t("common:table.noDevices")}>
                        {table.getRowModel().rows.map((row) => (
                            <HoverRow key={row.id} {...row} clickable={false}>
                                <TableRowCells row={row} />
                            </HoverRow>
                        ))}
                    </TableBody>
                </MinimalTable>
            </MinimalTableContainer>
            <CustomTablePagination
                component="div"
                className={classes.pagination}
                rowsPerPageOptions={[8, 24, 48]}
                count={table.getFilteredRowModel().rows.length}
                rowsPerPage={pageSize}
                page={pageIndex}
                onPageChange={(_event, page) => table.setPageIndex(page)}
                onRowsPerPageChange={(e) => {
                    const size = e.target.value ? Number(e.target.value) : 8;
                    table.setPageSize(size);
                }}
            />
        </SectionPanel>
    );
};
